// src/pages/Products.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { listProducts, deleteProductById } from "../services/productService";
import ProductForm from "../components/ProductForm";
import { ToastContainer, toast } from "react-toastify";

// ---------- PRIX (sans centimes) ----------
const parsePriceText = (v) => {
  if (v == null) return NaN;
  const s = String(v).replace(/[^\d]/g, "");
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : NaN;
};

const fmtInt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n));
const fmtXofFromNumber = (n) => (Number.isFinite(n) ? `${fmtInt(n)} FCFA` : "—");

const getDisplayPriceInfo = (p) => {
  const normalN =
    Number.isFinite(parsePriceText(p?.priceText))
      ? parsePriceText(p?.priceText)
      : Number.isFinite(Number(p?.price))
      ? Number(p.price)
      : NaN;

  const promoN = Number.isFinite(parsePriceText(p?.priceTextPromo))
    ? parsePriceText(p?.priceTextPromo)
    : NaN;

  const hasPromo =
    Number.isFinite(normalN) && Number.isFinite(promoN) && promoN > 0 && promoN < normalN;

  const display = hasPromo ? promoN : normalN;
  const pct = hasPromo ? Math.round(((normalN - promoN) / normalN) * 100) : 0;

  return { hasPromo, normalN, promoN, display, pct: Math.max(0, pct) };
};

// ---------- STOCK ----------
const stockLabel = (s) => {
  if (typeof s !== "number") return "—";
  if (s < 0) return "Non géré";
  return s;
};

const PAGE_SIZE = 10;

export default function Products() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // UI states
  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | draft | archived
  const [sortKey, setSortKey] = useState("new"); // new | old | priceAsc | priceDesc | stockAsc | stockDesc
  const formTopRef = useRef(null);
  const [focusKey, setFocusKey] = useState(0);

  const onEdit = (id) => {
    setEditingId(id);
    setFocusKey((k) => k + 1);          // ✅ déclenche focus
    requestAnimationFrame(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const load = async ({ reset }) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const res = await listProducts({
        pageSize: PAGE_SIZE,
        cursor: reset ? null : cursor,
        forAdmin: true,
      });

      setItems((prev) => {
        const next = reset ? res.items : [...prev, ...res.items];
        const map = new Map();
        next.forEach((p) => map.set(p.id, p));
        return Array.from(map.values());
      });

      setCursor(res.nextCursor || null);
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setEditingId(null);
    await load({ reset: true });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      setDeletingId(id);
      await deleteProductById(id);
      toast.success("Produit supprimé");
      setItems((list) => list.filter((p) => p.id !== id));
    } catch (e) {
      console.error(e);
      toast.error("Suppression impossible");
    } finally {
      setDeletingId(null);
    }
  };

  // Filter + Sort (client side)
  const filtered = useMemo(() => {
    const s = qText.trim().toLowerCase();
    let list = [...items];

    // Filter status
    if (statusFilter !== "all") {
      list = list.filter((p) => (p.status || "draft") === statusFilter);
    }

    // Search
    if (s) {
      list = list.filter((p) => {
        const title = String(p.title || "").toLowerCase();
        const ref = String(p.reference || "").toLowerCase();
        const code = String(p.code || "").toLowerCase();
        return title.includes(s) || ref.includes(s) || code.includes(s);
      });
    }

    // Sort
    const createdMillis = (p) => (p.createdAt?.toMillis ? p.createdAt.toMillis() : 0);

    list.sort((a, b) => {
      if (sortKey === "new") return createdMillis(b) - createdMillis(a);
      if (sortKey === "old") return createdMillis(a) - createdMillis(b);

      // ✅ prix affiché (promo si existe)
      const pa = getDisplayPriceInfo(a).display;
      const pb = getDisplayPriceInfo(b).display;

      if (sortKey === "priceAsc") return (pa || Number.POSITIVE_INFINITY) - (pb || Number.POSITIVE_INFINITY);
      if (sortKey === "priceDesc") return (pb || 0) - (pa || 0);

      const sa =
        typeof a.stock === "number"
          ? a.stock < 0
            ? Number.POSITIVE_INFINITY
            : a.stock
          : Number.POSITIVE_INFINITY;
      const sb =
        typeof b.stock === "number"
          ? b.stock < 0
            ? Number.POSITIVE_INFINITY
            : b.stock
          : Number.POSITIVE_INFINITY;

      if (sortKey === "stockAsc") return sa - sb;
      if (sortKey === "stockDesc") return sb - sa;

      return 0;
    });

    return list;
  }, [items, qText, statusFilter, sortKey]);

  const counts = useMemo(() => {
    const total = items.length;
    const active = items.filter((p) => p.status === "active").length;
    const draft = items.filter((p) => p.status === "draft").length;
    const archived = items.filter((p) => p.status === "archived").length;
    return { total, active, draft, archived };
  }, [items]);

  return (
    <div className="container py-4">
      <ToastContainer position="top-right" />

      <style>{`
        .soft-card { border: 0; box-shadow: 0 10px 22px rgba(0,0,0,.06); border-radius: 14px; }
        .table td, .table th { vertical-align: middle; }
        .img48 { width: 48px; height: 48px; object-fit: cover; border-radius: 10px; background:#f3f4f6; }
        .muted { color: #6c757d; }
        .price-main { font-weight: 900; color: #ff9800; letter-spacing:.2px; }
        .price-old { color:#8b8b8b; text-decoration: line-through; font-size: 12px; }
      `}</style>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="m-0">Produits</h4>
          <div className="muted small">
            Total: <b>{counts.total}</b> • Actifs: <b>{counts.active}</b> • Brouillons: <b>{counts.draft}</b> • Archivés: <b>{counts.archived}</b>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={refresh} disabled={loading || loadingMore}>
            Rafraîchir
          </button>

          <button className="btn btn-primary btn-sm" onClick={() => setEditingId(null)} disabled={loading}>
            + Nouveau produit
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* FORM */}
        <div className="col-lg-5">
          <div className="card soft-card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0">{editingId ? "Modifier le produit" : "Ajouter un produit"}</h5>
                {editingId && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingId(null)}>
                    Annuler
                  </button>
                )}
              </div>

              <ProductForm
                editingId={editingId}
                // ✅ tu vas ajouter ces champs dans le form (je te donne juste après)
                priceMode="text"
                focusKey={focusKey}          // ✅
                onSaved={async () => {
                  toast.success(editingId ? "Produit mis à jour" : "Produit ajouté");
                  setEditingId(null);
                  await load({ reset: true });
                }}
              />
              <div className="small text-muted mt-2">
                Prix recommandés : <b>priceText</b> (ex: “14 500”) et <b>priceTextPromo</b> (ex: “11 600”).
              </div>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="col-lg-7">
          <div className="card soft-card">
            <div className="card-body">
              {/* Toolbar */}
              <div className="row g-2 align-items-center mb-3">
                <div className="col-12 col-md-6">
                  <input
                    className="form-control"
                    placeholder="Rechercher (titre / référence / code)"
                    value={qText}
                    onChange={(e) => setQText(e.target.value)}
                  />
                </div>

                <div className="col-6 col-md-3">
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">Tous statuts</option>
                    <option value="active">Actifs</option>
                    <option value="draft">Brouillons</option>
                    <option value="archived">Archivés</option>
                  </select>
                </div>

                <div className="col-6 col-md-3">
                  <select className="form-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                    <option value="new">Tri: plus récents</option>
                    <option value="old">Tri: plus anciens</option>
                    <option value="priceAsc">Prix: croissant</option>
                    <option value="priceDesc">Prix: décroissant</option>
                    <option value="stockAsc">Stock: croissant</option>
                    <option value="stockDesc">Stock: décroissant</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="py-4 text-center">
                  <div className="spinner-border" role="status" />
                  <div className="muted small mt-2">Chargement des produits…</div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 70 }}>Image</th>
                          <th>Produit</th>
                          <th style={{ width: 190 }}>Prix</th>
                          <th style={{ width: 120 }}>Stock</th>
                          <th style={{ width: 110 }}>Statut</th>
                          <th style={{ width: 150 }} className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((p) => {
                          const lowStock = typeof p.stock === "number" && p.stock >= 0 && p.stock <= 3;
                          const pr = getDisplayPriceInfo(p);

                          return (
                            <tr key={p.id}>
                              <td>
                                {p.images?.[0] ? (
                                  <img src={p.images[0]} alt="img" className="img48" />
                                ) : (
                                  <div className="img48 d-grid place-items-center text-muted">—</div>
                                )}
                              </td>

                              <td>
                                <div className="fw-semibold">{p.title}</div>
                                <div className="muted small">{p.reference || p.code || "—"}</div>
                                {pr.hasPromo && pr.pct > 0 && (
                                  <span className="badge text-bg-danger mt-1">-{pr.pct}%</span>
                                )}
                              </td>

                              <td>
                                <div className="price-main">{fmtXofFromNumber(pr.display)}</div>
                                {pr.hasPromo && (
                                  <div className="price-old">{fmtXofFromNumber(pr.normalN)}</div>
                                )}
                              </td>

                              <td>
                                <span className={`badge ${lowStock ? "text-bg-warning" : "text-bg-secondary"}`}>
                                  {stockLabel(p.stock)}
                                </span>
                                {lowStock && <span className="muted small ms-2">stock bas</span>}
                              </td>

                              <td>
                                <span
                                  className={`badge text-bg-${
                                    p.status === "active" ? "success" : p.status === "draft" ? "secondary" : "dark"
                                  }`}
                                >
                                  {p.status || "draft"}
                                </span>
                              </td>

                              <td className="text-end">
                                <div className="btn-group">
                                  <button className="btn btn-sm btn-outline-primary" 
                                  onClick={() => onEdit(p.id)}
                                  //onClick={() => setEditingId(p.id)}
                                    >
                                    Éditer
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => onDelete(p.id)}
                                    disabled={deletingId === p.id}
                                  >
                                    {deletingId === p.id ? "..." : "Supprimer"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        {filtered.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center muted py-4">
                              Aucun produit trouvé.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Load more centré */}
                  <div className="d-flex justify-content-center">
                    <button
                      className="btn btn-outline-primary"
                      style={{ width: 180 }}
                      onClick={() => load({ reset: false })}
                      disabled={!cursor || loadingMore}
                    >
                      {loadingMore ? "Chargement..." : cursor ? "Charger plus" : "Fin de liste"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="muted small mt-2">
            Astuce: mets <b>stock = -1</b> si tu ne gères pas le stock, sinon un nombre (ex: 20).
          </div>
        </div>
      </div>
    </div>
  );
}
